import { useEffect } from "react";

/**
 * Lightweight SEO helper — sets <title>, meta description, OG tags
 * and a JSON-LD <script> block on the document head, cleaning up on unmount.
 */
export default function useSEO({ title, description, canonical, image, jsonLd }) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (name, content, attr = "name") => {
      if (!content) return null;
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      el.setAttribute("data-managed", "1");
      return el;
    };

    const created = [];
    const m1 = setMeta("description", description);
    if (m1) created.push(m1);
    const m2 = setMeta("og:title", title, "property");
    if (m2) created.push(m2);
    const m3 = setMeta("og:description", description, "property");
    if (m3) created.push(m3);
    const m4 = setMeta("og:type", "website", "property");
    if (m4) created.push(m4);
    if (image) {
      const m5 = setMeta("og:image", image, "property");
      if (m5) created.push(m5);
    }
    const m6 = setMeta("twitter:card", "summary_large_image");
    if (m6) created.push(m6);

    let canonicalEl = null;
    if (canonical) {
      canonicalEl = document.head.querySelector('link[rel="canonical"]');
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute("href", canonical);
    }

    let scriptEl = null;
    if (jsonLd) {
      scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.text = JSON.stringify(jsonLd);
      document.head.appendChild(scriptEl);
    }

    return () => {
      if (scriptEl) scriptEl.remove();
    };
  }, [title, description, canonical, image, JSON.stringify(jsonLd || {})]);
}
